"use client";

import { Languages } from "@alba-cars/common-modules";
import { Briefcase, Globe, Star } from "lucide-react";
import { useTranslations } from "next-intl";

import BrandExpertiseBadge from "./BrandExpertiseBadge";
import OptimizedImage from "../common/images/OptimizedImage";
import { Link } from "@/i18n/routing";
import { StaffMember } from "@/lib/api/sales-agent/get-sales-agents";
import { cn, formatImageUrl } from "@/lib/utils";

const DEFAULT_AGENT_IMAGE = "/assets/images/default-agent.svg";

interface SalesAgentCardProps {
  agent: StaffMember;
  showPreviousWorkplaces?: boolean;
  className?: string;
}

const formatLanguages = (languages: Languages[]): string => {
  return languages?.map((lang) => lang.replace(/["\[\]]/g, "")).join(", ") || "English";
};

const SalesAgentCard = ({ agent, showPreviousWorkplaces = false, className }: SalesAgentCardProps) => {
  const t = useTranslations("OurTeam.card");

  const formattedPhotoUrl = agent.user?.photo ? formatImageUrl(agent.user.photo) : null;
  const photoUrl = formattedPhotoUrl || DEFAULT_AGENT_IMAGE;
  const agentName = agent.user?.name || "Agent";
  const previousWorkplaces = agent.previousWorkplaces ?? [];
  const languages = formatLanguages(agent?.languages);
  const yearsOfExperience = agent.yearsOfExperience ?? null;

  return (
    <Link
      href={`/our-team/${agent.slug ?? agent.id}`}
      className={cn(
        "group flex h-full w-full flex-col overflow-hidden rounded-2xl bg-white transition-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
        className,
      )}
      tabIndex={0}
      aria-label={`View ${agentName}'s profile`}
    >
      <div className="relative h-72 flex-shrink-0">
        <OptimizedImage
          fill
          src={photoUrl}
          sizes="(max-width: 768px) 100vw, 375px"
          imageLayout="custom"
          width={350}
          maxWidth={500}
          alt={`${agentName} photo`}
          className="rounded-t-2xl object-contain object-top p-2"
        />
      </div>

      <div className="flex flex-1 flex-col px-4 py-2">
        <div className="mb-4">
          <h3 className="urbanist text-2xl font-bold text-primary-foreground">{agentName}</h3>
          {agent?.designation && <p className="inter text-sm font-normal text-black">{agent.designation}</p>}
        </div>

        {yearsOfExperience ? (
          <div className="flex items-center gap-3 pb-4">
            <Briefcase className="h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
            <span className="inter text-xs font-medium text-primary-foreground">
              {t("yearsOfExperience", { years: yearsOfExperience })}
            </span>
          </div>
        ) : null}

        {agent.languages ? (
          <div className="flex items-center gap-3 border-t border-gray-200 py-2">
            <Globe className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
            <div className="flex flex-col gap-1">
              <span className="inter text-xs font-normal text-[#292D3480]">{t("language")}</span>
              <span className="inter text-sm font-medium text-primary-foreground">{languages}</span>
            </div>
          </div>
        ) : null}

        {showPreviousWorkplaces && previousWorkplaces.length > 0 ? (
          <div className="border-t border-gray-200 pb-2 pt-4">
            <div className="mb-3 flex items-center justify-start gap-2">
              <Star className="h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
              <span className="inter text-sm font-medium text-primary-foreground">{t("brandExpertise")}</span>
            </div>
            <div className="flex flex-wrap items-center justify-start gap-2">
              {previousWorkplaces.map((workplace) => (
                <BrandExpertiseBadge key={workplace.slug} workplace={workplace} />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </Link>
  );
};

export default SalesAgentCard;
